import React, { useState, useContext } from 'react';

import moment from 'moment';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import classes from './styles.module.scss';
import getResizedImageUrl from '../../../../helpers/getResizedImageUrl';
import DefaultAvatar from '../../Candidates/CandidatesList/DefaultAvatar';
import AutoButton from '../../../AutoButton';
import ApplicationBadge from '../../../ApplicationBadge';
import DotMenu from '../../../ApplicationBadge/DotMenu';
import Tooltip from '../../../Tooltip';
import { UiContext } from '../../../../context/UiContext';
import AdminService from '../../../../services/AdminService';

export default function ApplicationCard({ data, onViewCandidate, refetch }) {
  const { t } = useTranslation();
  const { showNotification, setIsFetching } = useContext(UiContext);
  const [isDotMenuOpen, setIsDotMenuOpen] = useState(false);

  const eventDate = new Date(data.jobApplication.statusChangedAt);
  const isToday = moment(eventDate).isSame(new Date(), 'day');

  const calculateYearsOfExperience = () => {
    const { careerInfo } = data.jobApplication;
    if (!careerInfo?.careerExperiences?.length) return null;

    let totalYears = 0;
    careerInfo.careerExperiences.forEach((exp) => {
      if (exp.startDate) {
        const start = new Date(exp.startDate);
        const end = exp.endDate ? new Date(exp.endDate) : new Date();
        const years = (end - start) / (1000 * 60 * 60 * 24 * 365.25);
        totalYears += years;
      }
    });

    return Math.round(totalYears);
  };

  const yearsOfExperience = calculateYearsOfExperience();

  const getLastCompany = () => {
    const { careerInfo } = data.jobApplication;
    if (!careerInfo?.careerExperiences?.length) return null;

    // Sort by startDate descending (most recent first), or use current job first
    const sortedExperiences = [...careerInfo.careerExperiences].sort((a, b) => {
      // Prioritize current jobs
      if (a.isCurrent && !b.isCurrent) return -1;
      if (!a.isCurrent && b.isCurrent) return 1;

      // Then sort by startDate descending
      const aDate = a.startDate ? new Date(a.startDate) : new Date(0);
      const bDate = b.startDate ? new Date(b.startDate) : new Date(0);
      return bDate - aDate;
    });

    const lastExperience = sortedExperiences[0];
    return lastExperience?.empoyerName || lastExperience?.employerName || null;
  };

  const lastCompany = getLastCompany();

  const getTopSkills = () => {
    const { careerInfo } = data.jobApplication;
    if (!careerInfo?.skills?.length) return [];
    return careerInfo.skills.slice(0, 2);
  };

  const topSkills = getTopSkills();

  const changeJobApplicationStatus = async (jobApplicationId, status, undo) => {
    try {
      setIsFetching(true);

      await AdminService.changeMyJobApplicationStatus({
        jobApplicationId,
        status: status.status,
        subStatus: status.subStatus,
      });
      await refetch();
      const stageTitle = {
        Screen: t(
          'dashboardComponents.Candidate.CandidateHeader.stageTitles.Screening'
        ),
        Interview: t(
          'dashboardComponents.Candidate.CandidateHeader.stageTitles.Interview'
        ),
        ClientSubmit: t(
          'dashboardComponents.Candidate.CandidateHeader.stageTitles.ClientSubmit'
        ),
        ClientInterview: t(
          'dashboardComponents.Candidate.CandidateHeader.stageTitles.ClientInterview'
        ),
        Offer: t(
          'dashboardComponents.Candidate.CandidateHeader.stageTitles.Offer'
        ),
        Onboard: t(
          'dashboardComponents.Candidate.CandidateHeader.stageTitles.Onboarding'
        ),
      };

      if (status.subStatus === 'Hire' || status.subStatus === 'Hold') {
        const titleText =
          status.subStatus === 'Hire'
            ? 'Candidate approved for hire. The recruiter has been notified.'
            : 'Candidate placed On Hold. You can resume review anytime.';
        showNotification({
          text: `${titleText}`,
          undo,
        });
      } else {
        showNotification({
          text: `${t(
            'dashboardComponents.Candidate.CandidateHeader.Candidate successfully moved to'
          )} ${stageTitle[status.status]}`,
          undo,
        });
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsFetching(false);
    }
  };

  const renderActionButton = () => {
    return (
      <AutoButton
        variant="blackWhite"
        height={40}
        onClick={(e) => {
          e.stopPropagation();
          onViewCandidate(data);
        }}
      >
        {t('common.view')}
      </AutoButton>
    );
  };

  return (
    <div
      className={classNames(classes.Card, {
        [classes.cardMenuOpen]: isDotMenuOpen,
      })}
    >
      <div className={classes.border} />
      <div
        className={classes.cardContent}
        onClick={() => onViewCandidate(data)}
      >
        <div className={classes.rowTop}>
          <div className={classes.avatar}>
            {data.jobApplication.user?.userProfile?.avatar ? (
              <img
                src={getResizedImageUrl({
                  url: `avatar/${data.jobApplication.user?.userProfile?.avatar}`,
                  width: 60,
                })}
                alt="Candidate"
              />
            ) : (
              <DefaultAvatar
                firstName={data.jobApplication.user?.userProfile?.firstName}
                lastName={data.jobApplication.user?.userProfile?.lastName}
                width={60}
                height={60}
              />
            )}
          </div>
          <div className={classes.titleBlock}>
            <div className={classes.title}>
              {data.jobApplication.user?.userProfile?.firstName}{' '}
              {data.jobApplication.user?.userProfile?.lastName}
            </div>
            <div className={classes.subtitle}>
              {data.jobApplication.jobPost?.jobTitle}
            </div>
          </div>
          <div className={classes.badgesContainer}>
            {data.jobApplication.isPrioritized && (
              <span className={classes.priorityBadge}>Priority</span>
            )}
            <div className={classes.badge}>
              <ApplicationBadge jobApplication={data.jobApplication} />
            </div>
          </div>
          <DotMenu
            changeJobApplicationStatus={changeJobApplicationStatus}
            refetch={refetch}
            jobApplication={data.jobApplication}
            variant="medium"
            onOpenChange={setIsDotMenuOpen}
          />
        </div>
        <div className={classes.rowBottom}>
          <div className={classes.meta}>
            <Tooltip text={moment(eventDate).format('DD MMM YYYY')}>
              <span className={classes.chip}>
                {isToday ? t('common.today') : moment(eventDate).fromNow()}
              </span>
            </Tooltip>
            {data.jobApplication.jobApplicationTags?.length > 0 &&
              data.jobApplication.jobApplicationTags.map((tag) => (
                <span key={tag.id} className={classes.chip}>
                  {tag.label || tag.name}
                </span>
              ))}
            {yearsOfExperience !== null && yearsOfExperience > 0 && (
              <span className={classes.chip}>{yearsOfExperience}+ years</span>
            )}
            {lastCompany && <span className={classes.chip}>{lastCompany}</span>}
            {topSkills.map((skill) => (
              <span key={skill.id} className={classes.chip}>
                {skill.name}
              </span>
            ))}
            {data.jobApplication.careerInfo?.immediateJoiner && (
              <span className={classes.chip}>
                {t(
                  'dashboardComponents.Candidate.Details.availability.immediateJoiner'
                )}
              </span>
            )}
          </div>
          <div className={classes.actions}>{renderActionButton()}</div>
        </div>
      </div>
    </div>
  );
}
